import { lightDataUpdate } from '../../../redux/Slice/gridSlice';
import store from '../../../redux/store';
import { toDegrees } from '../../../utils/UtilsFunction';
import * as Three from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import {
  applyHdriToSceneVisual,
  getActiveViewportCamera,
  syncHdriBackgroundForCamera,
} from '../../../utils/ThreeJsUtils/hdriSceneBackground.js';
import { applyGridTransformUpdate } from '../../../utils/ThreeJsUtils/syncObjectTransform';
import { disableTransformControlsShadows } from '../../../utils/ThreeJsUtils/lightShadowUtils.js';

let toRemove = [];

export function setCurrentCamera(
  newCamera,
  { currentCamera, controlsOrbit, transformControls, rendererRef, scene }
) {
  // update reference
  currentCamera = newCamera;

  // Update orbit controls
  if (controlsOrbit) {
    controlsOrbit.object = newCamera;
    controlsOrbit.update();
  }

  // Update transform controls
  if (transformControls) {
    transformControls.camera = newCamera;
  }

  // Render with new camera
  if (rendererRef?.current) {
    rendererRef.current.render(scene, newCamera);
  }

  return currentCamera; // return updated camera
}

/** Resolve the scene light from a helper or light object (supports multiple lights of same type). */
export function findLightFromHelperIntersect(parent, scene) {
  if (!parent) return parent;

  const isHelper =
    parent.name?.endsWith('-helper') ||
    (typeof parent.type === 'string' && parent.type.endsWith('Helper'));

  if (isHelper) {
    const lightId = parent.name?.replace(/-helper$/, '');
    if (lightId && scene) {
      const byName = scene.getObjectByName(lightId);
      if (byName?.isLight) return byName;
    }
  }

  return parent;
}

//******************Directional Light Helper********************* */
export function findDirectionalLightFromIntersects(parent, scene) {
  return findLightFromHelperIntersect(parent, scene);
}

//******************Point Light Helper********************* */
export function findPointLightFromIntersects(parent, scene) {
  return findLightFromHelperIntersect(parent, scene);
}

//******************Spot Light Helper********************* */
export function findSpotLightFromIntersects(parent, scene) {
  return findLightFromHelperIntersect(parent, scene);
}

//******************Area Light Helper********************* */
export function findAreaLightFromIntersects(parent, scene) {
  return findLightFromHelperIntersect(parent, scene);
}

//*****************ASSETS UPLOADED FUNCTIONS********************** */
export const uploadOptions = [
  {
    id: 'popupImage',
    title: 'Import Image',
    inputId: 'imageUpload',
    accept: '.jpg, .jpeg, .png',
    cancelId: 'cancelImage',
    cancelHandler: CancelBtnImage,
  },
  {
    id: 'popupModel',
    title: 'Import GLB',
    inputId: '3dUpload',
    accept: '.glb',
    cancelId: 'cancelModel',
    cancelHandler: CancelBtnModel,
  },
  {
    id: 'popupVideo',
    title: 'Import Video',
    inputId: 'videoUpload',
    accept: '.mp4',
    cancelId: 'cancelVideo',
    cancelHandler: CancelBtnVideo,
  },
];

export function handleCancelClick() {
  document.getElementById('popupImage').style.display = 'none';
  document.getElementById('popupVideo').style.display = 'none';
  document.getElementById('popupModel').style.display = 'none';
}

export function CancelBtnImage() {
  var cancelButton = document.getElementById('cancelImage');
  cancelButton?.removeEventListener('click', handleCancelClick);
  cancelButton?.addEventListener('click', handleCancelClick);
}

export function CancelBtnVideo() {
  var cancelButton = document.getElementById('cancelVideo');
  cancelButton.removeEventListener('click', handleCancelClick);
  cancelButton.addEventListener('click', handleCancelClick);
}

export function CancelBtnModel() {
  var cancelButton = document.getElementById('cancelModel');
  cancelButton?.removeEventListener('click', handleCancelClick);
  cancelButton?.addEventListener('click', handleCancelClick);
}

export function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}
// Debounced Redux dispatch for light updates
export const debouncedLightUpdate = debounce((updatedLight) => {
  try {
    store.dispatch(
      lightDataUpdate({ actionType: 'update', light: updatedLight })
    );
  } catch (error) {
    console.error('Light update error:', error);
  }
}, 16); // 16ms debounce (~60fps) for smooth continuous updates during Transform Controls dragging

// Debounced Redux update for particles
export const debouncedParticleUpdate = debounce((updates, selectedObject) => {
  try {
    if (selectedObject?.deleteType === 'particle' && selectedObject.name) {
      store.dispatch({
        type: 'particles/updateParticle',
        payload: {
          id: selectedObject.name,
          updates,
        },
      });
    }
  } catch (error) {
    console.error('Particle update error:', error);
  }
}, 150); // Increased debounce time for better performance

// Sync mesh/hotspot/image/etc. transforms to Redux after gizmo or panel edits
export const debouncedGridTransformUpdate = debounce((object) => {
  try {
    const { window_type } = store.getState().window;
    if (window_type !== 'design' || !object) return;
    applyGridTransformUpdate(object);
  } catch (error) {
    console.error('Grid transform sync error:', error);
  }
}, 150);

// Utility to remove all user assets from the scene (except grid, camera, helpers)
export function clearSceneAssets(scene, transformControls) {
  toRemove = [];
  scene.traverse((obj) => {
    // Remove all except grid, camera, helpers, and transformControls
    if (
      !obj.isLight && // Do NOT remove any lights (Ambient, Directional, Point, Spot, etc.)
      obj.type !== 'GridHelper' &&
      obj.type !== 'Camera' &&
      obj.type !== 'PerspectiveCamera' &&
      obj.type !== 'OrthographicCamera' &&
      obj.type !== 'DirectionalLightHelper' &&
      obj.type !== 'PointLightHelper' &&
      obj.type !== 'SpotLightHelper' &&
      obj.name !== 'hdriBackgroundSphere' &&
      obj.name !== 'mrCenterCube' &&
      !obj.userData?.isHdriBackground &&
      obj !== transformControls && // Don't remove transformControls!
      obj.parent === scene // Only top-level
    ) {
      toRemove.push(obj);
    }
  });
  toRemove.forEach((obj) => {
    scene.remove(obj);
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
      else obj.material.dispose();
    }
  });
  // Ensure transformControls is in the scene
  if (transformControls && !scene.children.includes(transformControls)) {
    scene.add(transformControls);
    disableTransformControlsShadows(transformControls);
  }
}

//**********************GET POLYCOUNT FUNCTION*********************** */
export function getPolyCount(scene) {
  let totalPolyCount = 0;
  let totalObjectCount = 0;
  scene?.traverse((object) => {
    if (
      object.type === 'GridHelper' ||
      object.type === 'Camera' ||
      object.type === 'Line' ||
      object.userData?.isMrCenterCube ||
      object.type.includes('Light')
    )
      return;

    if ((object.isMesh || object.isPoints) && object.visible && object.geometry) {
      totalObjectCount++;

      const geometry = object.geometry;
      if (object.isMesh) {
        if (geometry.index?.count) {
          totalPolyCount += geometry.index.count / 3;
        } else if (geometry.attributes?.position?.count) {
          totalPolyCount += geometry.attributes.position.count / 3;
        }
      } else if (object.isPoints) {
        // For particles, we count each point as 1 toward the polycount
        if (geometry.attributes?.position?.count) {
          totalPolyCount += geometry.attributes.position.count;
        }
      }
    }
  });

  return totalPolyCount;
}

// Cache DOM elements for better performance
export function getUIElements(cachedUIElements) {
  if (!cachedUIElements) {
    cachedUIElements = {
      positionX: document.getElementById('positionX'),
      positionY: document.getElementById('positionY'),
      positionZ: document.getElementById('positionZ'),
      rotationX: document.getElementById('rotationX'),
      rotationY: document.getElementById('rotationY'),
      rotationZ: document.getElementById('rotationZ'),
      scaleX: document.getElementById('scaleX'),
      scaleY: document.getElementById('scaleY'),
      scaleZ: document.getElementById('scaleZ'),
    };
  }
  return cachedUIElements;
}

// Safe value for number inputs: Infinity/NaN are invalid and cause "cannot be parsed" errors.
// Also normalize the output (trim trailing zeros) so imperative DOM writes don't fight
// React-controlled inputs that use "1" vs "1.00" style formatting.
function safeNumberInput(value, decimals, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  if (decimals != null) return parseFloat(num.toFixed(decimals)).toString();
  return String(num);
}

// Function to update UI elements with object properties
export function UpdateUI(object, cachedUIElements) {
  const state = store.getState();
  const { window_type } = state.window;
  if (!object || window_type != 'design') return;

  const elements = getUIElements(cachedUIElements);

  // Batch DOM updates for better performance
  const updates = [];

  const activeInputId = document.activeElement?.id;
  const isInputActive = (id) => activeInputId === id;

  // Transform input fields are controlled by React in AppRightSection.
  // Avoid imperative DOM writes here to prevent value thrashing/flicker
  // between this legacy updater and React state updates.

  // Batch execute all DOM updates
  if (updates.length > 0) {
    requestAnimationFrame(() => {
      updates.forEach((update) => update());
    });
  }

  // If the object is a particle, ensure its transform controls are also updated
  if (object.deleteType === 'particle') {
    // Removed Redux dispatch to avoid infinite update loop
    // This logic is already covered above, but you can add any particle-specific UI sync here if needed
  }
}

//******************HDRI HANDLER FUNCTION********************** */
export const handleHdriSelected = (
  file,
  urll,
  rotationDegrees = 0,
  scene,
  onLoadedCallback,
  hdriVisible = true,
  camera = null,
  lightIntensity = 1
) => {
  const applyHdriToScene = (envMap) => {
    envMap.wrapS = envMap.wrapT = Three.RepeatWrapping;
    const activeCamera = camera ?? getActiveViewportCamera();
    applyHdriToSceneVisual(
      scene,
      envMap,
      rotationDegrees,
      hdriVisible,
      activeCamera,
      null,
      lightIntensity
    );
    syncHdriBackgroundForCamera(scene, activeCamera);

    if (onLoadedCallback && typeof onLoadedCallback === 'function') {
      onLoadedCallback(envMap);
    }
  };

  if (urll) {
    new RGBELoader().load(urll, (envMap) => {
      applyHdriToScene(envMap);
    });
  } else if (file) {
    const url = URL.createObjectURL(file);
    new RGBELoader().load(url, (envMap) => {
      applyHdriToScene(envMap);
      URL.revokeObjectURL(url);
    });
  }
};
